import { forwardRef } from "react";
import {
  CheckboxGroupProps as CheckboxGroupPropsPrimitiveProps,
  CheckboxGroup as CheckboxGroupPrimitive,
} from "react-aria-components";

import { FormHelperText } from "@/components/FormHelperText/FormHelperText";
import { AstreriskProps, InputLabel } from "@/components/InputLabel/InputLabel";
import { Stack } from "@/components/Stack/Stack";

import styles from "./CheckboxGroup.module.scss";

export interface CheckboxGroupProps extends CheckboxGroupPropsPrimitiveProps, AstreriskProps {
  /** The current value (controlled). */
  value?: CheckboxGroupPropsPrimitiveProps["value"];
  /** The default value (uncontrolled). */
  defaultValue?: CheckboxGroupPropsPrimitiveProps["defaultValue"];
  /** Whether the input is disabled. */
  isDisabled?: CheckboxGroupPropsPrimitiveProps["isDisabled"];
  /** Whether the input can be selected but not changed by the user. */
  isReadOnly?: CheckboxGroupPropsPrimitiveProps["isReadOnly"];
  /**
   * The name of the input element, used when submitting an HTML form. See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefname).
   */
  name?: CheckboxGroupPropsPrimitiveProps["name"];
  /** Whether user input is required on the input before form submission. */
  isRequired?: CheckboxGroupPropsPrimitiveProps["isRequired"];
  /** Whether the input value is invalid. */
  isInvalid?: CheckboxGroupPropsPrimitiveProps["isInvalid"];
  /** Handler that is called when the value changes. */
  onChange?: CheckboxGroupPropsPrimitiveProps["onChange"];
  /**
   * The content to display as the label.
   */
  label?: React.ReactNode;
  /**
   * A description for the field. Provides a hint such as specific requirements for what to choose.
   */
  description?: React.ReactNode;
  /**
   * An error message for the field.
   */
  errorMessage?: React.ReactNode;

  /**
   * The axis the checkbox should align with. Two options in a row.
   * @default vertical
   */
  orientation?: "vertical" | "horizontal";
  children?: React.ReactNode;
}

/**
 * A checkbox group allows a user to select multiple items from a list of options.
 *
 * ## State
 * ### Uncontrolled
 * A CheckboxGroup's `value` is empty by default, but an initial, uncontrolled, value can be provided using the `defaultValue` prop.
 *
 * ```tsx
 * <CheckboxGroup label="Greeting" defaultValue={["Hello"]} />
 * ```
 *
 * ### Controlled
 * The `value` prop can be used to make the `value` controlled. The `onChange` event is fired when the user selects an option, and receives the new `value`.
 *
 * ```tsx
 * const [value, setValue] = React.useState("")
 * <CheckboxGroup label="Greeting" value={value} onChange={setValue} />
 * ```
 */

export const CheckboxGroup = /*#__PURE__*/ (function () {
  const CheckboxGroup = forwardRef<HTMLDivElement, CheckboxGroupProps>(
    (
      {
        children,
        label,
        isInvalid,
        description,
        errorMessage,
        orientation,
        showAsterisk,
        ...props
      },
      ref
    ) => {
      const hasDescription = Boolean(description);
      const hasErrorMessage = Boolean(errorMessage);
      const showErrorMessage = isInvalid && hasErrorMessage;
      const showDescription = hasDescription && !showErrorMessage;
      const isHorizontal = orientation === "horizontal";

      return (
        <Stack direction="column" spacing="3xs">
          <CheckboxGroupPrimitive {...props} isInvalid={isInvalid} ref={ref}>
            <Stack direction="column" spacing="3xs">
              {label && <InputLabel showAsterisk={showAsterisk}>{label}</InputLabel>}

              <Stack
                spacing="3xs"
                flexWrap="wrap"
                direction={isHorizontal ? "row" : "column"}
                className={isHorizontal ? styles.isHorizontal : undefined}
              >
                {children}
              </Stack>
            </Stack>
          </CheckboxGroupPrimitive>

          {showDescription && <FormHelperText>{description}</FormHelperText>}
          {showErrorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        </Stack>
      );
    }
  );

  CheckboxGroup.displayName = "CheckboxGroup";
  return CheckboxGroup;
})();
